import { useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { AccessType, ObjectType, UpdateAccessType } from 'services/access.service';

import { enumToOptions } from 'components/app/_helpers/app-config.helper';
import { selectFinalCut, selectProject, selectStory, updateAccessType } from 'components/app/_mediator/app-data.slice';
import { setAccessTypeModal } from 'components/app/projects/_mediator/projects.slice';

import { Modal } from 'components/UI/modal/modal';
import { InputGroup } from 'components/UI/input/input-group';
import { Select } from 'components/UI/select/select';
import { Loader } from 'components/UI/loader/loader';

import styles from './_elements.module.scss';

const OPTIONS = enumToOptions(AccessType);

type Data = UpdateAccessType & { name: string };

export const AccessConfigModal = () => {
  const accessTypeModal = useAppSelector((state) => state.projects.accessTypeModal);

  const projectData = useAppSelector(selectProject);
  const storyData = useAppSelector(selectStory);
  const fcData = useAppSelector(selectFinalCut);

  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  const data: Data | undefined = useMemo(() => {
    if (accessTypeModal === null) return;
    if (accessTypeModal === ObjectType.Project) {
      if (!projectData) return;
      return {
        objectId: projectData.projectId,
        objectType: accessTypeModal,
        accessType: projectData.accessType,
        name: projectData.name,
      };
    }
    if (accessTypeModal === ObjectType.Story) {
      if (!storyData) return;
      return {
        objectId: storyData.storyId,
        objectType: accessTypeModal,
        accessType: storyData.accessType,
        name: storyData.name,
      };
    }
    if (accessTypeModal === ObjectType.FinalCut) {
      if (!fcData) return;
      return {
        objectId: fcData.id,
        objectType: accessTypeModal,
        accessType: fcData.accessType,
        name: fcData.name,
      };
    }
  }, [accessTypeModal]);

  const closeModal = () => {
    dispatch(setAccessTypeModal(null));
  };

  const handleChange = async (value: AccessType, data: Data) => {
    setLoading(true);
    await dispatch(updateAccessType({ ...data!, accessType: value })).finally(() => {
      setLoading(false);
      closeModal();
    });
  };

  if (!data) return;

  return (
    <Modal className={styles.AccessConfigModal} onRequestClose={closeModal} isOpen={accessTypeModal !== null}>
      <p>
        <span>{ObjectType[data.objectType]}</span> name: <b>{data.name}</b>
      </p>
      <div>
        <span>Locked - the entity cannot be changed or deleted</span>
        <span>Protected - the entity cannot be deleted</span>
      </div>
      <InputGroup header="Access type">
        {loading ? (
          <Loader className={styles.AccessConfigModal__Loader} />
        ) : (
          <Select
            options={OPTIONS}
            value={data.accessType}
            onChange={(e) => handleChange(parseInt(e.target.value), data)}
          />
        )}
      </InputGroup>
    </Modal>
  );
};
